import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { Emitter, type Event } from '@patsnap/tahiti-core/common/event';
import { GetBasicByIdDTO } from '../patent-repository';
import { OVER_VIEW_LIMIT_ERROR_MESSAGE, DEFAULT_PATENT_BASIC_DATA } from '../../constants';
import { getFieldValueByLang } from '../../utils/get-field-value-by-lang';
import type { PatentBasicEntity } from '../../types';

export const PatentBasicService = Symbol('PatentBasicService');

export interface PatentBasicService {
    readonly onOverLimit: Event<void>;

    getPatentBase(patentId: string): Promise<PatentBasicEntity>;
    getPatentDefaultOfAbst(patent: PatentBasicEntity): { lang: string, text: string };
    getPatentDefaultOfTitle(patent: PatentBasicEntity): { lang: string, text: string };
}

@injectable()
export class PatentBasicServiceImpl implements PatentBasicService {
    private _onOverLimitEmitter = new Emitter<void>();
    readonly onOverLimit = this._onOverLimitEmitter.event;
    private _cache = new Map<string, PatentBasicEntity>();

    constructor(
        @inject(GetBasicByIdDTO)
        private _getBasicByIdDTO: GetBasicByIdDTO,
    ) {
    }

    private _setEntityCache(patentId: string, data: PatentBasicEntity) {
        this._cache.set(patentId, data);
    }

    private _getEntityCache(patentId: string) {
        return this._cache.get(patentId);
    }

    public async getPatentBase(patentId: string) {
        const cacheData = this._getEntityCache(patentId);

        if (cacheData) return cacheData;

        try {
            const result = await this._getBasicByIdDTO.getBasicById(patentId);
            this._setEntityCache(patentId, result);
            return result;
        } catch (e: unknown) {
            if (e instanceof Error && e.message === OVER_VIEW_LIMIT_ERROR_MESSAGE) {
                // TODO: 限制查看
                // TODO: 限制查看
                // TODO: 限制查看
                this._onOverLimitEmitter.fire();
            }
            return DEFAULT_PATENT_BASIC_DATA;
        }
    }

    public getPatentDefaultOfAbst(data: PatentBasicEntity) {
        const lang = data.ABST_LANG || data.LANG_DEFAULT || '';
        const text = getFieldValueByLang(data.ABST, lang).value || '';

        return { lang, text };
    }

    public getPatentDefaultOfTitle(data: PatentBasicEntity) {
        const lang = data.TITLE_LANG || data.LANG_DEFAULT || '';
        const text = getFieldValueByLang(data.TITLE, lang).value || '';

        return { lang, text };
    }
}

